import { ComponentProps, FC, useEffect } from "react";

import { Box } from "@mui/material";

import LiveBadge from "../../../../../../common/components/icons/LiveBadge";
import MediaStatus from "../../../../../../common/components/media/MediaStatus";
import useHlsStream, {
  MAX_RETRIES,
} from "../../../../../../common/hooks/useHLSStream";
import LiveStreamPlayer from "./LiveStreamPlayer";

interface HLSStreamComponentProps {
  height?: string;
  videoLoading?: boolean;
  setVideoLoading?: (value: boolean) => void;
  setVideoError?: () => void;
  liveBadgePlacement?: "left" | "right";
  fixedHeight?: string;
  providedAspectRatio?: number;
}

const HLSStreamPlayer: FC<HLSStreamComponentProps> = ({
  videoLoading,
  setVideoLoading,
  setVideoError,
  liveBadgePlacement = "left",
  fixedHeight,
}): JSX.Element => {
  const { loading, hlsUrl, retryCount, needRetry, retry, setLoading } =
    useHlsStream();

  const renderMediaStatus = (
    statusProps: Omit<ComponentProps<typeof MediaStatus>, "minHeight">
  ): JSX.Element => (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      <MediaStatus {...statusProps} minHeight={fixedHeight} />
    </Box>
  );

  const handleRetry = (): void => {
    setLoading(true);

    if (setVideoLoading) {
      setVideoLoading(true);
    }

    retry();
  };

  useEffect(() => {
    if (setVideoLoading) {
      const videoNotFound =
        !needRetry && !hlsUrl && !loading && retryCount >= MAX_RETRIES;

      setVideoLoading(loading || videoNotFound || !hlsUrl);

      if (!hlsUrl && retryCount >= MAX_RETRIES && !loading && setVideoError) {
        setVideoError();
      }
    }
  }, [loading, hlsUrl, retryCount, needRetry]);

  const shouldShowConnecting =
    loading || (!needRetry && !hlsUrl && !loading && retryCount < MAX_RETRIES);

  if (shouldShowConnecting) {
    return renderMediaStatus({
      variant: "connecting",
      retryCount,
      maxRetries: MAX_RETRIES,
    });
  }

  const shouldShowNotFound =
    needRetry ||
    (!needRetry && !hlsUrl && !loading && retryCount >= MAX_RETRIES);

  if (shouldShowNotFound) {
    return renderMediaStatus({
      variant: "notFound",
      onRetry: handleRetry,
    });
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: fixedHeight ?? "100%",
        position: "relative",
        borderRadius: "8px",
        overflow: "hidden",
        backgroundColor: "black",
      }}
    >
      {!loading && !needRetry && !videoLoading && hlsUrl && (
        <LiveBadge
          sx={{
            position: "absolute",
            top: "8px",
            ...(liveBadgePlacement === "right"
              ? { right: "8px" }
              : { left: "8px" }),
          }}
        />
      )}

      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#000",
        }}
      >
        <LiveStreamPlayer streamUrl={hlsUrl} setVideoError={setVideoError} />
      </Box>
    </Box>
  );
};

export default HLSStreamPlayer;
